import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dashboardPathForRole } from "../lib/roleRoutes";

const ForcePasswordChange = () => {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) nav("/entrar", { replace: true });
    if (!loading && user && !user.requires_password_change) {
      nav(dashboardPathForRole(user.role), { replace: true });
    }
  }, [loading, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("A senha precisa ter ao menos 6 caracteres.");
    if (password !== confirm) return toast.error("As senhas não conferem.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: password });
      toast.success("Senha atualizada com sucesso.");
      // reload user so requires_password_change=false + hard reload garante AuthContext limpo
      const { data } = await api.get("/auth/me");
      window.location.replace(dashboardPathForRole(data.role));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Sua sessão expirou. Faça login novamente.");
        setTimeout(() => nav("/entrar", { replace: true }), 1200);
      } else {
        toast.error(err?.response?.data?.detail || "Erro ao atualizar senha.");
      }
    } finally { setBusy(false); }
  };

  if (loading || !user) return <div className="p-10 text-slate-500">Carregando…</div>;

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-md bg-[#0B2B62] text-white flex items-center justify-center">
            <KeyRound className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Segurança</div>
            <h1 className="font-display font-bold text-2xl text-[#0B2B62] tracking-tight">Definir nova senha</h1>
          </div>
        </div>

        <p className="text-sm text-slate-600 mt-3">
          Olá, <b>{user.name}</b>. Sua conta foi aprovada pela equipe RAF com uma senha temporária.
          Por segurança, defina agora uma senha pessoal para continuar.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Nova senha</label>
            <input
              data-testid="force-pwd-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={6}
              required
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]"
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Confirmar senha</label>
            <input
              data-testid="force-pwd-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={6}
              required
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]"
              placeholder="Repita a nova senha"
            />
          </div>

          <button
            data-testid="force-pwd-submit"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0B2B62] hover:bg-[#0A2540] text-white font-semibold px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
          >
            <ShieldCheck className="w-4 h-4" /> {busy ? "Salvando…" : "Salvar nova senha"}
          </button>
        </form>

        <button
          data-testid="force-pwd-logout"
          onClick={async () => { await logout(); nav("/entrar", { replace: true }); }}
          className="mt-4 text-xs text-slate-500 hover:text-[#0B2B62]"
        >
          Sair e voltar ao login
        </button>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
